import { memo, useEffect, useState } from "react";
import type { BookingTarget } from "../../booking/types";
import { ActivitySectionFrame } from "../../provider/components/ActivitySectionFrame";
import { TeleconsultoriaDateFilter } from "../../shared/components/TeleconsultoriaDateFilter";
import type { TeleconsultoriaDateRange } from "../../shared/hooks/useTeleconsultoriaDateRange";
import { useAffiliateBookingOptions } from "../hooks/useAffiliateBookingOptions";
import { TeleconsultorChoiceCard } from "./TeleconsultorChoiceCard";

/**
 * Marcar teleconsultoria (AFILIADA) em 3 toques: data → teleconsultor → horário.
 * Teleconsultores disponíveis agora e horários das agendas ficam na mesma lista.
 * Usado dentro do painel "Marcar teleconsultoria" (o título geral fica no cabeçalho
 * dele). `range` vem do painel: ele abre no dia da lista ou do horário a trocar.
 */
export const AffiliateBookingSection = memo(function AffiliateBookingSection({
  refreshKey,
  onBook,
  range,
}: {
  refreshKey: number;
  onBook: (target: BookingTarget) => void;
  range: TeleconsultoriaDateRange;
}) {
  const { choices, presence, isLoading, hasError, reload } = useAffiliateBookingOptions(
    range,
    refreshKey,
  );
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Um só teleconsultor com horários: já abre os horários dele (menos um toque).
  useEffect(() => {
    const withSlots = choices.filter((choice) => choice.slots.length);
    if (withSlots.length === 1) setSelectedKey(withSlots[0].key);
  }, [choices]);

  const handleToggle = (key: string) =>
    setSelectedKey((current) => (current === key ? null : key));

  return (
    <ActivitySectionFrame
      title="Teleconsultores da prestadora"
      subtitle="Escolha o teleconsultor e depois o horário."
      toolbar={<TeleconsultoriaDateFilter range={range} />}
      isLoading={isLoading}
      hasError={hasError}
      errorText="Não foi possível carregar os teleconsultores da prestadora."
      isEmpty={!choices.length}
      emptyText="Nenhum teleconsultor com horários neste período nem disponível agora. Tente outro dia."
      onRefresh={reload}
    >
      {choices.map((choice) => (
        <TeleconsultorChoiceCard
          key={choice.key}
          choice={choice}
          presenceStatus={presence[choice.teleconsultor.reference]}
          isSelected={selectedKey === choice.key}
          onToggle={handleToggle}
          onBook={onBook}
        />
      ))}
    </ActivitySectionFrame>
  );
});
